import { useEffect, useState } from 'react';
import CustomModal from './AddressModal';
import DeliveryAddress from './AddressSection';
import ImageSlider from './ImageSlider';
import Icons from './Icons';
import ProductDetails from './ProductDetails';
import { usePhysicalDeliveryStore } from 'store/DeliveryStore';
import { userStore } from 'store/UserStore';
import BuyCoinsBarsService from '../../services/buy-coins-bars.service';
import { toast } from 'react-toastify';
import '../../styles/Step2.css';
import Loader from 'shared/common/loader';
import BillingAddressModal from './BillingAddressModal';
import KycPopup from 'shared/common/kycPopup';

const Step2 = ({ nextStep, prevStep }) => {
  const [loading, setLoading] = useState(false);
  const [pincode, setPincode] = useState('');
  const [modalShow, setModalShow] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [billingAddress, setBillingAddress] = useState([]);
  const [billingModalShow, setBillingModalShow] = useState(false);
  const [isKYCRequired, setIsKYCRequired] = useState(false);

  const { selectedProduct, selectedAddress } = usePhysicalDeliveryStore(
    (state) => ({
      selectedProduct: state.selectedProduct,
      selectedAddress: state.selectedAddress,
    })
  );

  const [customerId, user, customer] = userStore((state: any) => [
    state.customerId,
    state.user,
    state.customer,
  ]);

  const setSummary = usePhysicalDeliveryStore(
    (state) => state.setQuotationSummary
  );

  // const checkAvailability = async () => {
  //   if (!pincode || pincode.length !== 6) {
  //     setMessage('Please enter a valid 6-digit pincode.');
  //     return;
  //   }

  //   try {
  //     const response = await BuyCoinsBarsService.checkIfPincodeIsServiceable(pincode);
  //     const serviceable = response?.data?.data?.serviceable;

  //     if (serviceable) {
  //       setMessage(response.data.data.Message || 'Pincode is serviceable');
  //     } else {
  //       setMessage(
  //         response.data.data.Message ||
  //         'Sorry, delivery is not available at this pincode.'
  //       );
  //     }
  //   } catch (error) {
  //     toast.error('Something went wrong!');
  //     console.error('Error checking service availability:', error);
  //   }
  // };

  const checkAvailability = async (pincodeToCheck?: string) => {
    try {
      const response = await BuyCoinsBarsService.checkIfPincodeIsServiceable(
        pincodeToCheck || pincode
      );
      const serviceable = response?.data?.data;
      return { serviceable };
    } catch (error) {
      toast.error("Something went wrong!");
      console.error("Error checking service availability:", error);
      return { serviceable: false };
    }
  };


  const fetchAllAddresses = async () => {
    const response = await BuyCoinsBarsService.getDeliveryAddress(customerId);
    if (response?.status === 200) {
      setAddresses(response?.data?.data);
      usePhysicalDeliveryStore
        .getState()
        .setSelectedAddress(response.data.data[0]);
    }
  };


  const proceedToCart = async () => {
    const payload = {
      productId: selectedProduct?.itemCode,
      qty: '1',
      ebActRef: customer?.accounts[0]?.accountId,
      pincode: selectedAddress?.pinCode,
      userId: user?.userId,
      digitalUsedQty: 0,
    };

    setLoading(true);
    try {
      const addToCartResponse = await BuyCoinsBarsService.addProductToCart(
        payload
      );
      if (addToCartResponse.status === 200) {
        const summaryResponse = await BuyCoinsBarsService.getProductCartSummery(
          user.userId
        );
        if (summaryResponse.status === 202) {
          setSummary(summaryResponse?.data?.data[0] || {});
          nextStep();
        } else {
          toast.error('Failed to fetch cart summary.');
        }
      } else {
        toast.error('Failed to add product to cart.');
      }
    } catch (error) {
      toast.error(
        'This pincode is not serviceable. Please update the delivery address.'
      );
    } finally {
      setLoading(false);
    }
  };

  const checkPanRequirement = async () => {
    try {
      if (!customer?.kyc?.pan?.isVerified) {
        setIsKYCRequired(true); // open KYC modal
        return false;
      }
      return true;
    } catch (err) {
      console.error("Error checking PAN requirement:", err);
      return false;
    }
  };

  const onKycModalClose = () => {
    setIsKYCRequired(false);
  };


  const handleProceedToCheckout = async () => {
    if (
      !selectedProduct?.itemCode ||
      !customer?.accounts[0]?.accountId ||
      !selectedAddress?.pinCode ||
      !user?.userId
    ) {
      toast.error("Missing required information to proceed.");
      return;
    }

    // Step 1: Check PAN before continuing
    const isPanVerified = await checkPanRequirement();
    if (!isPanVerified) return;

    setLoading(true);
    try {
      // Step 2: Check billing
      const billingCheckResponse =
        await BuyCoinsBarsService.checkBillingAddress();

      const isBillingAddressRequired =
        billingCheckResponse?.data?.data?.isBillingAddressRequired;

      if (billingCheckResponse && isBillingAddressRequired) {
        setBillingAddress(billingCheckResponse?.data?.data?.customerDetails);
        setBillingModalShow(true);
        toast.info("Please add a billing address to continue.");
        return; // stop here
      }

      // Step 3: Proceed if everything is okay
      await proceedToCart();
    } catch (error) {
      toast.error("Something went wrong while proceeding to checkout.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllAddresses();
  }, []);

  useEffect(() => {
    const prefillPincode =
      selectedAddress?.pinCode || customer?.accounts?.[0]?.shippingAddress?.pinCode;
    if (prefillPincode) {
      setPincode(prefillPincode);
    }
  }, [selectedAddress, customer]);

  useEffect(() => {
    if (Array.isArray(selectedProduct) && selectedProduct.length === 0) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [selectedProduct]);

  return (
    <div className='step2-main-container'>
      {loading && <Loader />}
      <div className='step2-left'>
        <ImageSlider images={selectedProduct?.image || []} />
        <Icons />
      </div>

      <ProductDetails
        product={selectedProduct}
        pincode={pincode}
        setPincode={setPincode}
        checkAvailability={checkAvailability}
      />

      <div className='delivery-address'>
        <h3>Delivery Address</h3>
        <DeliveryAddress
          addresses={addresses}
          selectedAddress={selectedAddress}
          setModalShow={setModalShow}
        />
      </div>

      <div className='checkout-container'>
        <button className='checkout-btn back-btn' onClick={prevStep}>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='14'
            height='14'
            fill='currentColor'
            viewBox='0 0 16 16'
            className='back-icon'
          >
            <path
              fillRule='evenodd'
              d='M11 2a.5.5 0 0 1 0 .707L6.207 7.5H14.5a.5.5 0 0 1 0 1H6.207l4.793 4.793a.5.5 0 0 1-.707.707l-5.5-5.5a.5.5 0 0 1 0-.707l5.5-5.5A.5.5 0 0 1 11 2z'
            />
          </svg>
          Previous
        </button>

        <button
          className="checkout-btn proceed-btn"
          onClick={() => {
            if (!selectedAddress) {
              toast.error("Please add a delivery address to proceed.");
              return;
            }
            handleProceedToCheckout();
          }}
          disabled={loading}
        >
          Proceed to checkout
        </button>
      </div>

      <CustomModal
        show={modalShow}
        addresses={addresses}
        onClose={() => setModalShow(false)}
        onAddressAdded={fetchAllAddresses}
      />

      <BillingAddressModal
        show={billingModalShow}
        onClose={() => setBillingModalShow(false)}
        customerDetails={billingAddress}
        onAddressAdded={proceedToCart}
      />

      <KycPopup
        isKYCPopupDisplayed={isKYCRequired}
        isPanVerficationRequired={true}
        closeModal={onKycModalClose}
        hideModal={onKycModalClose}
      />
    </div>
  );
};

export default Step2;
